import { Link } from "react-router-dom";
import {
  adminMobileActionPrimaryClass,
  adminMobileActionSecondaryClass,
  adminMobileCardActionsClass,
  adminMobileCardBodyClass,
  adminMobileCardClass,
  adminMobileCardHeaderClass,
  adminMobileCardMetaClass,
  adminMobileCardTitleClass,
  adminMobileListClass,
  adminMobileRowClass,
  adminMobileRowLabelClass,
  adminMobileRowValueClass,
  adminSpinnerSmClass,
} from "../admin/adminStyles";
import { formatMoney } from "../../utils/formatMoney";
import { formatUkDateTime } from "../../utils/dateUtils";
import { shortenVehicleTypeName } from "../../utils/vehicleTypeLabel";

export interface BookingListItem {
  id: string;
  bookingId: number;
  pasengerName: string;
  pickupDateTime: Date | null;
  status: string;
  paymentStatus: string;
  amount: number | string | null;
  vehicleTypeName: string;
  bookingSource: string | null;
}

const BOOKING_STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "confirmed", label: "Confirmed" },
  { value: "assigned", label: "Assigned" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "Paid", label: "Paid" },
  { value: "cancelled", label: "Cancelled" },
] as const;

const PAYMENT_STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "completed", label: "Completed" },
  { value: "paid", label: "Paid" },
  { value: "failed", label: "Failed" },
  { value: "refunded", label: "Refunded" },
] as const;

function bookingReference(booking: BookingListItem): string {
  if (booking.bookingId > 0) return String(booking.bookingId);
  return booking.id.slice(0, 8).toUpperCase();
}

function statusSelectClass(status: string): string {
  const base =
    "h-8 max-w-full cursor-pointer rounded-full border-0 px-2.5 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-brand-500/30 disabled:cursor-wait disabled:opacity-60";
  switch (status.toLowerCase()) {
    case "completed":
    case "paid":
      return `${base} bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-400`;
    case "pending":
      return `${base} bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-400`;
    case "cancelled":
    case "failed":
      return `${base} bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-400`;
    case "confirmed":
    case "assigned":
    case "in_progress":
    case "refunded":
      return `${base} bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-300`;
    default:
      return `${base} bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-gray-300`;
  }
}

function ensureOption(
  options: readonly { value: string; label: string }[],
  current: string,
): { value: string; label: string }[] {
  if (!current) return [...options];
  if (
    options.some(
      (o) =>
        o.value === current || o.value.toLowerCase() === current.toLowerCase(),
    )
  ) {
    return [...options];
  }
  return [{ value: current, label: current }, ...options];
}

function formatDate(date: Date | null): string {
  if (!date) return "N/A";
  return formatUkDateTime(date);
}

type Props = {
  bookings: BookingListItem[];
  loading: boolean;
  onOpen: (id: string) => void;
  onStatusChange?: (booking: BookingListItem, status: string) => void | Promise<void>;
  onPaymentStatusChange?: (
    booking: BookingListItem,
    paymentStatus: string,
  ) => void | Promise<void>;
  updatingStatusId?: string | null;
  updatingPaymentId?: string | null;
};

export default function BookingsListCards({
  bookings,
  loading,
  onOpen,
  onStatusChange,
  onPaymentStatusChange,
  updatingStatusId,
  updatingPaymentId,
}: Props) {
  if (loading) {
    return (
      <div className={`${adminMobileListClass} items-center py-10`}>
        <div className={adminSpinnerSmClass} />
        <p className="text-sm text-gray-500">Loading bookings…</p>
      </div>
    );
  }

  if (bookings.length === 0) {
    return (
      <div className={`${adminMobileListClass} py-10 text-center text-sm text-gray-500`}>
        No bookings found
      </div>
    );
  }

  return (
    <div className={adminMobileListClass}>
      {bookings.map((booking) => (
        <article
          key={booking.id}
          role="button"
          tabIndex={0}
          onClick={() => onOpen(booking.id)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onOpen(booking.id);
            }
          }}
          className={`${adminMobileCardClass} cursor-pointer`}
        >
          <div className={adminMobileCardHeaderClass}>
            <div className="min-w-0">
              <div className="font-mono text-[12px] font-medium tracking-wide text-brand-600 dark:text-brand-400">
                #{bookingReference(booking)}
              </div>
              <h3 className={adminMobileCardTitleClass}>
                {booking.pasengerName || "—"}
              </h3>
              <p className={adminMobileCardMetaClass}>
                {formatDate(booking.pickupDateTime)}
              </p>
            </div>
            <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
              <select
                value={booking.status || "pending"}
                disabled={updatingStatusId === booking.id || !onStatusChange}
                onChange={(e) => void onStatusChange?.(booking, e.target.value)}
                className={statusSelectClass(booking.status || "pending")}
                aria-label={`Booking status for ${bookingReference(booking)}`}
              >
                {ensureOption(BOOKING_STATUS_OPTIONS, booking.status).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={adminMobileCardBodyClass}>
            <div className={adminMobileRowClass}>
              <span className={adminMobileRowLabelClass}>Vehicle</span>
              <span className={adminMobileRowValueClass}>
                {shortenVehicleTypeName(booking.vehicleTypeName) || "—"}
              </span>
            </div>
            <div className={adminMobileRowClass}>
              <span className={adminMobileRowLabelClass}>Amount</span>
              <span className={adminMobileRowValueClass}>
                {formatMoney(booking.amount)}
              </span>
            </div>
            <div className={adminMobileRowClass}>
              <span className={adminMobileRowLabelClass}>Website</span>
              <span className={adminMobileRowValueClass}>
                {booking.bookingSource || "—"}
              </span>
            </div>
            <div className={adminMobileRowClass}>
              <span className={adminMobileRowLabelClass}>Payment</span>
              <span
                className={adminMobileRowValueClass}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <select
                  value={booking.paymentStatus || "pending"}
                  disabled={updatingPaymentId === booking.id || !onPaymentStatusChange}
                  onChange={(e) =>
                    void onPaymentStatusChange?.(booking, e.target.value)
                  }
                  className={statusSelectClass(booking.paymentStatus || "pending")}
                  aria-label={`Payment status for ${bookingReference(booking)}`}
                >
                  {ensureOption(PAYMENT_STATUS_OPTIONS, booking.paymentStatus).map(
                    (opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ),
                  )}
                </select>
              </span>
            </div>
          </div>

          <div
            className={adminMobileCardActionsClass}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <Link
              to={`/bookings/${booking.id}/view`}
              className={adminMobileActionSecondaryClass}
            >
              View
            </Link>
            <Link
              to={`/bookings/edit/${booking.id}`}
              className={adminMobileActionPrimaryClass}
            >
              Edit
            </Link>
          </div>
        </article>
      ))}
    </div>
  );
}
